import * as React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  Field,
  Input,
  Select,
  Toast,
  ToastTitle,
  Toaster,
  useId,
  useToastController,
} from "@fluentui/react-components";
import {
  Add20Regular,
  ArrowSync20Regular,
  Checkmark20Regular,
  Delete20Regular,
  Dismiss20Regular,
  Dismiss24Regular,
  Edit20Regular,
  Search20Regular,
} from "@fluentui/react-icons";
import styles from "./Settup.module.css";

/* ------------------------------------------------------------------ */
/* Finance & Operations setup screens.                                 */
/* Same gateway, headers and routes as the F&O workspace (Ops.ts).     */
/* Chart of accounts, banks, tax codes and the ledger pages belong to  */
/* the accounting service: they are in Accounting Setup, not here.     */
/* Roles and users are in Roles & Permissions.                         */
/* ------------------------------------------------------------------ */

type Rec = { id: string } & Record<string, any>;
type Intent = "success" | "error" | "warning";
type Opt = { v: string; l: string };
type Lk = "sites" | "workers" | "items";

interface Fld {
  key: string;
  label: string;
  kind: "text" | "number" | "date" | "select";
  options?: Opt[];
  lookup?: Lk;
  /** a Yes/No choice stored as true/false */
  bool?: boolean;
  required?: boolean;
  full?: boolean;
  /** cannot be changed once the record exists */
  lock?: boolean;
  hint?: string;
}
interface Col {
  key: string;
  label: string;
  kind?: "mono" | "status" | "money" | "date" | "num" | "lookup";
  lookup?: Lk;
  right?: boolean;
  get?: (r: Rec) => any;
}
interface Ctx {
  site: string;
  lk: Record<Lk, Map<string, string>>;
}
interface Entity {
  title: string;
  subtitle: string;
  noun: string;
  needs?: Lk[];
  load: () => Promise<Rec[]>;
  cols: Col[];
  fields: Fld[];
  readOnly?: boolean;
  /** create is available */
  create?: (v: Record<string, any>, rows: Rec[], ctx: Ctx) => Promise<void>;
  /** edit mutates the full record, which is then written back whole */
  edit?: { path: string; apply: (e: Rec, v: Record<string, any>) => void };
  remove?: string;
  toggle?: string;
  defaults?: Record<string, any>;
  toForm?: (r: Rec) => Record<string, any>;
  validate?: (v: Record<string, any>) => string | null;
}

/* ---------- gateway client (same contract as Api/Api.ts) ---------- */

const env = ((import.meta as unknown as { env?: Record<string, string | undefined> }).env ?? {}) as Record<string, string | undefined>;
const DEFAULT_BASE = (env.VITE_FO_GATEWAY_URL || "https://fo.cilpron.com").replace(/\/+$/, "");
const DEFAULT_COMPANY = env.VITE_FO_COMPANY_ID || "11111111-1111-1111-1111-111111111111";
const TOKEN_KEYS = [env.VITE_FO_TOKEN_KEY, "fo_access_token", "access_token", "hcm_access_token", "token"].filter((k): k is string => !!k);

const storedToken = (): string => {
  for (const store of [sessionStorage, localStorage]) {
    for (const k of TOKEN_KEYS) {
      try {
        const v = store.getItem(k);
        if (v) return v.replace(/^"|"$/g, "");
      } catch {
        /* storage blocked */
      }
    }
  }
  return "";
};

class FoError extends Error {}

const problem = (status: number, body: any): string => {
  if (body?.errors && typeof body.errors === "object") {
    const first = Object.entries(body.errors as Record<string, string[]>)[0];
    if (first && first[1]?.[0]) return `${first[0]}: ${first[1][0]}`;
  }
  return body?.detail || body?.title || (status === 401 ? "You are not signed in." : status === 403 ? "You do not have permission for this." : `Request failed (${status})`);
};

const makeApi = (base: string, company: string, token: string) => {
  async function call<T = any>(method: string, path: string, body?: unknown): Promise<T> {
    const t = token || storedToken();
    let res: Response;
    try {
      res = await fetch(`${base}/${path.replace(/^\//, "")}`, {
        method,
        headers: {
          Accept: "application/json",
          ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
          ...(t ? { Authorization: `Bearer ${t}` } : {}),
          "X-Company-Id": company,
        },
        body: body !== undefined ? JSON.stringify(body) : undefined,
      });
    } catch {
      throw new FoError(`Cannot reach the F&O gateway at ${base}.`);
    }
    if (res.status === 204) return undefined as T;
    const text = await res.text();
    let json: any = null;
    try {
      json = text ? JSON.parse(text) : null;
    } catch {
      json = text;
    }
    if (!res.ok) throw new FoError(problem(res.status, json));
    return json as T;
  }
  const api = {
    get: <T = any>(p: string) => call<T>("GET", p),
    post: <T = any>(p: string, b?: unknown) => call<T>("POST", p, b ?? {}),
    put: <T = any>(p: string, b: unknown) => call<T>("PUT", p, b),
    del: (p: string) => call<void>("DELETE", p),
    /** every row of a (small) resource, following the pages */
    all: async (path: string): Promise<Rec[]> => {
      const out: Rec[] = [];
      for (let page = 1; page <= 40; page++) {
        const r = await call<any>("GET", `${path}?page=${page}&pageSize=200`);
        const items: Rec[] = Array.isArray(r) ? r : r?.items ?? [];
        out.push(...items);
        if (Array.isArray(r) || page >= Number(r?.totalPages ?? 1)) break;
      }
      return out;
    },
  };
  return api;
};
type Api = ReturnType<typeof makeApi>;

/* ---------- small helpers ---------- */

const S = (x: unknown): string => (x === null || x === undefined ? "" : String(x));
const N = (x: unknown): number => Number(x) || 0;
const D = (x: unknown): string => S(x).slice(0, 10);
const today = (): string => new Date().toISOString().slice(0, 10);
const errText = (e: unknown) => (e instanceof Error && e.message ? e.message : "Something went wrong");
const money = (v: number) => {
  try {
    return new Intl.NumberFormat("en-NG", { style: "currency", currency: "NGN", maximumFractionDigits: 0 }).format(v || 0);
  } catch {
    return String(Math.round(v || 0));
  }
};
const nextCode = (prefix: string, existing: string[]): string => {
  const mx = existing.reduce((m, c) => (c.startsWith(prefix) ? Math.max(m, Number(c.slice(prefix.length)) || 0) : m), 0);
  return prefix + String(mx + 1).padStart(4, "0");
};
const cap = (s: string) => `${s[0]?.toUpperCase() ?? ""}${s.slice(1)}`;
const TONE: Record<string, string> = { Active: "green", Inactive: "grey", Draft: "grey", Blocked: "amber", Terminated: "red", Suspended: "amber", Obsolete: "grey", Merged: "grey" };
const Pill: React.FC<{ text: string }> = ({ text }) => <span className={`${styles.accPill} ${styles["accTone_" + (TONE[text] || "grey")]}`}>{text}</span>;
const activeLabel = (r: Rec) => (r.isActive === false ? "Inactive" : "Active");

/** read -> change -> write back (PUT overwrites everything, so the full record goes back) */
const change = async (api: Api, path: string, id: string, fn: (e: Rec) => void): Promise<void> => {
  const e = await api.get<Rec>(`${path}/${id}`);
  fn(e);
  await api.put(`${path}/${id}`, e);
};

/* ---------- screens ---------- */

const ALL_MODULES = ["ai", "assets", "clm", "commerce", "controltower", "cpq", "extensibility", "industry", "insights", "intercompany", "inventory", "manufacturing", "masterdata", "pim", "planning", "plm", "procurement", "projects", "quality", "sales", "sustainability", "trade", "warehouse", "workforce"];

const build = (api: Api): Record<string, Entity> => ({
  legal: {
    title: "Legal entities",
    subtitle: "The companies in your group. Each one has its own currency, country and time zone.",
    noun: "legal entity",
    load: () => api.all("api/masterdata/companies"),
    cols: [
      { key: "code", label: "Code", kind: "mono" },
      { key: "name", label: "Name" },
      { key: "countryCode", label: "Country" },
      { key: "baseCurrency", label: "Currency" },
      { key: "isActive", label: "Status", kind: "status", get: activeLabel },
    ],
    fields: [
      { key: "code", label: "Code", kind: "text", required: true, lock: true, hint: "Cannot be changed later" },
      { key: "name", label: "Name", kind: "text", required: true },
      { key: "countryCode", label: "Country code", kind: "text", required: true, hint: "Two letters, for example NG" },
      { key: "baseCurrency", label: "Currency", kind: "text", required: true, hint: "Three letters, for example NGN" },
    ],
    defaults: { countryCode: "NG", baseCurrency: "NGN" },
    toForm: (r) => ({ code: S(r.code), name: S(r.name), countryCode: S(r.countryCode), baseCurrency: S(r.baseCurrency) }),
    create: async (v) => {
      await api.post("api/masterdata/companies", { code: S(v.code), name: S(v.name), legalName: S(v.name), baseCurrency: S(v.baseCurrency) || "NGN", countryCode: S(v.countryCode) || "NG", defaultCulture: "en-NG", timeZoneId: "Africa/Lagos", isActive: true });
    },
    edit: { path: "api/masterdata/companies", apply: (e, v) => Object.assign(e, { name: S(v.name), legalName: S(v.name), countryCode: S(v.countryCode), baseCurrency: S(v.baseCurrency) }) },
    remove: "api/masterdata/companies",
    toggle: "api/masterdata/companies",
  },

  units: {
    title: "Operating units",
    subtitle: "Departments and divisions, with the person who manages each one.",
    noun: "operating unit",
    needs: ["sites", "workers"],
    load: () => api.all("api/workforce/org-units"),
    cols: [
      { key: "code", label: "Code", kind: "mono" },
      { key: "name", label: "Name" },
      { key: "type", label: "Type" },
      { key: "manager", label: "Manager", kind: "lookup", lookup: "workers", get: (r) => r.managerWorkerId },
    ],
    fields: [
      { key: "name", label: "Name", kind: "text", required: true, full: true },
      { key: "type", label: "Type", kind: "text", required: true, hint: "For example Department" },
      { key: "managerWorkerId", label: "Manager", kind: "select", lookup: "workers" },
    ],
    defaults: { type: "Department" },
    create: async (v, rows, ctx) => {
      await api.post("api/workforce/org-units", { code: nextCode("OU-", rows.map((u) => S(u.code))), name: S(v.name), type: S(v.type) || "Department", managerWorkerId: v.managerWorkerId || null, siteId: ctx.site, effectiveFrom: today() });
    },
  },

  warehouses: {
    title: "Warehouses",
    subtitle: "Where stock is kept. Receipts, issues and transfers use these.",
    noun: "warehouse",
    needs: ["sites"],
    load: async () => (await api.all("api/masterdata/locations")).filter((l) => S(l.kind) === "Warehouse"),
    cols: [
      { key: "code", label: "Code", kind: "mono" },
      { key: "name", label: "Name" },
      { key: "storeType", label: "Type" },
      { key: "placement", label: "Location" },
      { key: "isActive", label: "Status", kind: "status", get: activeLabel },
    ],
    fields: [
      { key: "name", label: "Name", kind: "text", required: true, full: true },
      { key: "storeType", label: "Type", kind: "text", hint: "For example Main or Cold room" },
      { key: "placement", label: "Location", kind: "text" },
    ],
    toForm: (r) => ({ name: S(r.name), storeType: S(r.storeType), placement: S(r.placement) }),
    create: async (v, rows, ctx) => {
      await api.post("api/masterdata/locations", { siteId: ctx.site, code: nextCode("WH-", rows.map((l) => S(l.code))), name: S(v.name), kind: "Warehouse", storeType: S(v.storeType), placement: S(v.placement), isActive: true });
    },
    edit: { path: "api/masterdata/locations", apply: (e, v) => Object.assign(e, { name: S(v.name), storeType: S(v.storeType), placement: S(v.placement) }) },
    remove: "api/masterdata/locations",
    toggle: "api/masterdata/locations",
  },

  partners: {
    title: "Business partners",
    subtitle: "Suppliers and customers in the F&O master data. Accounting keeps its own customer and vendor lists.",
    noun: "partner",
    load: () => api.all("api/masterdata/partners"),
    cols: [
      { key: "code", label: "Code", kind: "mono" },
      { key: "name", label: "Name" },
      { key: "kind", label: "Kind" },
      { key: "partnerGroup", label: "Group" },
      { key: "paymentTermsDays", label: "Terms (days)", kind: "num", right: true },
      { key: "creditLimit", label: "Credit limit", kind: "money", right: true },
      { key: "status", label: "Status", kind: "status" },
    ],
    fields: [
      { key: "name", label: "Name", kind: "text", required: true, full: true },
      { key: "kind", label: "Kind", kind: "select", options: ["Supplier", "Customer", "Both"].map((v) => ({ v, l: v })), required: true, lock: true },
      { key: "partnerGroup", label: "Group", kind: "text" },
      { key: "email", label: "Email", kind: "text", required: true },
      { key: "phone", label: "Phone", kind: "text" },
      { key: "taxId", label: "Tax ID", kind: "text", required: true },
      { key: "paymentTermsDays", label: "Payment terms (days)", kind: "number" },
      { key: "creditLimit", label: "Credit limit", kind: "number", hint: "Customers only" },
    ],
    defaults: { kind: "Supplier", paymentTermsDays: 30 },
    toForm: (r) => ({ name: S(r.name), kind: S(r.kind), partnerGroup: S(r.partnerGroup), email: S(r.email), phone: S(r.phone), taxId: S(r.taxId), paymentTermsDays: N(r.paymentTermsDays), creditLimit: N(r.creditLimit) }),
    create: async (v, rows) => {
      const kind = S(v.kind) || "Supplier";
      const code = nextCode(kind === "Supplier" ? "V-" : kind === "Customer" ? "C-" : "P-", rows.map((p) => S(p.code)));
      const p = await api.post("api/masterdata/partners", {
        code, name: S(v.name), legalName: S(v.name), kind, partnerGroup: S(v.partnerGroup), paymentTermsDays: N(v.paymentTermsDays), phone: S(v.phone),
        countryCode: "NG", currencyCode: "NGN", taxId: S(v.taxId), email: S(v.email), ...(kind !== "Supplier" ? { creditLimit: N(v.creditLimit) } : {}),
      });
      await api.post(`api/masterdata/partners/${p.id}/activate`).catch(() => undefined);
    },
    edit: {
      path: "api/masterdata/partners",
      apply: (e, v) => Object.assign(e, { name: S(v.name), legalName: S(v.name), partnerGroup: S(v.partnerGroup), email: S(v.email), phone: S(v.phone), taxId: S(v.taxId), paymentTermsDays: N(v.paymentTermsDays), ...(S(e.kind) !== "Supplier" ? { creditLimit: N(v.creditLimit) } : {}) }),
    },
    remove: "api/masterdata/partners",
  },

  items: {
    title: "Items",
    subtitle: "Products and services you buy, stock or sell.",
    noun: "item",
    load: () => api.all("api/masterdata/items"),
    cols: [
      { key: "code", label: "Code", kind: "mono" },
      { key: "name", label: "Name" },
      { key: "itemType", label: "Type" },
      { key: "baseUoM", label: "Unit" },
      { key: "standardCost", label: "Standard cost", kind: "money", right: true },
      { key: "status", label: "Status", kind: "status" },
    ],
    fields: [
      { key: "name", label: "Name", kind: "text", required: true, full: true },
      { key: "itemType", label: "Type", kind: "text", required: true, lock: true, hint: "For example Stocked" },
      { key: "baseUoM", label: "Unit", kind: "text", required: true, lock: true, hint: "For example EA" },
      { key: "standardCost", label: "Standard cost", kind: "number" },
      { key: "isPurchasable", label: "Can be bought", kind: "select", bool: true, options: [{ v: "true", l: "Yes" }, { v: "false", l: "No" }] },
      { key: "isSellable", label: "Can be sold", kind: "select", bool: true, options: [{ v: "true", l: "Yes" }, { v: "false", l: "No" }] },
    ],
    defaults: { itemType: "Stocked", baseUoM: "EA", isPurchasable: "true", isSellable: "false" },
    toForm: (r) => ({ name: S(r.name), itemType: S(r.itemType), baseUoM: S(r.baseUoM), standardCost: N(r.standardCost), isPurchasable: String(r.isPurchasable !== false), isSellable: String(r.isSellable === true) }),
    create: async (v, rows) => {
      const code = nextCode("IT-", rows.map((i) => S(i.code)));
      const it = await api.post("api/masterdata/items", {
        code, name: S(v.name), itemType: S(v.itemType) || "Stocked", baseUoM: S(v.baseUoM) || "EA", standardCost: N(v.standardCost), costCurrency: "NGN", costingMethod: "Average",
        isPurchasable: v.isPurchasable === true, isSellable: v.isSellable === true, tracking: "None", gtin: `GTIN${Date.now()}`.slice(0, 14),
      });
      await api.post(`api/masterdata/items/${it.id}/activate`).catch(() => undefined);
    },
    edit: { path: "api/masterdata/items", apply: (e, v) => Object.assign(e, { name: S(v.name), standardCost: N(v.standardCost), isPurchasable: v.isPurchasable === true, isSellable: v.isSellable === true }) },
    remove: "api/masterdata/items",
  },

  reorder: {
    title: "Reorder policies",
    subtitle: "When stock of an item falls to the reorder point, it shows up as low.",
    noun: "reorder policy",
    needs: ["items", "sites"],
    load: () => api.all("api/inventory/reorder-policies"),
    cols: [
      { key: "item", label: "Item", kind: "lookup", lookup: "items", get: (r) => r.itemId },
      { key: "itemCode", label: "Code", kind: "mono" },
      { key: "method", label: "Method" },
      { key: "reorderPoint", label: "Reorder point", kind: "num", right: true },
      { key: "enabled", label: "Status", kind: "status", get: (r) => (r.enabled === false ? "Inactive" : "Active") },
    ],
    fields: [
      { key: "itemId", label: "Item", kind: "select", lookup: "items", required: true, lock: true, full: true },
      { key: "reorderPoint", label: "Reorder point", kind: "number", required: true },
      { key: "enabled", label: "Enabled", kind: "select", bool: true, options: [{ v: "true", l: "Yes" }, { v: "false", l: "No" }] },
    ],
    defaults: { enabled: "true" },
    toForm: (r) => ({ itemId: S(r.itemId), reorderPoint: N(r.reorderPoint), enabled: String(r.enabled !== false) }),
    validate: (v) => (N(v.reorderPoint) <= 0 ? "Reorder point must be greater than zero." : null),
    create: async (v, _rows, ctx) => {
      const it = await api.get<Rec>(`api/masterdata/items/${S(v.itemId)}`);
      await api.post("api/inventory/reorder-policies", { itemId: v.itemId, itemCode: S(it.code), siteId: ctx.site, method: "ReorderPoint", enabled: v.enabled !== false, reorderPoint: N(v.reorderPoint) });
    },
    edit: { path: "api/inventory/reorder-policies", apply: (e, v) => Object.assign(e, { reorderPoint: N(v.reorderPoint), enabled: v.enabled !== false }) },
  },

  workers: {
    title: "Workers",
    subtitle: "Employees and contractors. Timesheets and leave refer to these.",
    noun: "worker",
    needs: ["sites"],
    load: () => api.all("api/workforce/workers"),
    cols: [
      { key: "code", label: "Code", kind: "mono" },
      { key: "fullName", label: "Name" },
      { key: "departmentCode", label: "Department", get: (r) => S(r.departmentCode) || S(r.orgUnitCode) },
      { key: "jobTitle", label: "Position" },
      { key: "hireDate", label: "Hired", kind: "date" },
      { key: "monthlySalary", label: "Monthly pay", kind: "money", right: true },
      { key: "status", label: "Status", kind: "status" },
    ],
    fields: [
      { key: "fullName", label: "Name", kind: "text", required: true, full: true },
      { key: "departmentCode", label: "Department", kind: "text" },
      { key: "jobTitle", label: "Position", kind: "text" },
      { key: "hireDate", label: "Hire date", kind: "date", required: true },
      { key: "monthlySalary", label: "Monthly pay", kind: "number", required: true },
    ],
    defaults: { hireDate: today() },
    toForm: (r) => ({ fullName: S(r.fullName), departmentCode: S(r.departmentCode) || S(r.orgUnitCode), jobTitle: S(r.jobTitle), hireDate: D(r.hireDate), monthlySalary: N(r.monthlySalary) }),
    validate: (v) => (N(v.monthlySalary) < 0 ? "Pay can't be negative." : null),
    create: async (v, rows, ctx) => {
      await api.post("api/workforce/workers", {
        code: nextCode("W-", rows.map((w) => S(w.code))), fullName: S(v.fullName), siteId: ctx.site, departmentCode: S(v.departmentCode), jobTitle: S(v.jobTitle), type: "Employee",
        hireDate: S(v.hireDate), currencyCode: "NGN", monthlySalary: N(v.monthlySalary), hourlyRate: Math.round(N(v.monthlySalary) / 173), weeklyHours: 40, overtimeMultiplier: 1.5,
      });
    },
    edit: {
      path: "api/workforce/workers",
      apply: (e, v) => Object.assign(e, { fullName: S(v.fullName), departmentCode: S(v.departmentCode), jobTitle: S(v.jobTitle), hireDate: S(v.hireDate), monthlySalary: N(v.monthlySalary), hourlyRate: Math.round(N(v.monthlySalary) / 173) }),
    },
    remove: "api/workforce/workers",
  },

  numseq: {
    title: "Number sequences",
    subtitle: "The next number each module will use. Modules create these the first time they number a record.",
    noun: "sequence",
    readOnly: true,
    load: async () => {
      const lists = await Promise.all(ALL_MODULES.map((m) => api.get<Rec[]>(`api/${m}/admin/number-sequences`).catch(() => [] as Rec[])));
      return lists.flat().map((e) => ({ ...e, id: `${S(e.module)}:${S(e.key)}`, label: `${S(e.module)}` }));
    },
    cols: [
      { key: "module", label: "Module" },
      { key: "key", label: "Prefix", kind: "mono" },
      { key: "next", label: "Next number", kind: "num", right: true },
    ],
    fields: [],
  },
});

/** Sidebar structure for Settup.tsx. */
export const FO_SETUP_GROUPS: { title: string; items: { key: string; title: string }[] }[] = [
  { title: "Organization", items: [{ key: "legal", title: "Legal entities" }, { key: "units", title: "Operating units" }, { key: "warehouses", title: "Warehouses" }] },
  { title: "Master data", items: [{ key: "partners", title: "Business partners" }, { key: "items", title: "Items" }, { key: "reorder", title: "Reorder policies" }] },
  { title: "People & system", items: [{ key: "workers", title: "Workers" }, { key: "numseq", title: "Number sequences" }] },
];

/* ---------- record dialog ---------- */

const initialOf = (f: Fld, rec: Rec | null, defaults: Record<string, any>, form: Record<string, any> | null): string => {
  const raw = rec ? form?.[f.key] : defaults[f.key];
  return raw === undefined || raw === null ? "" : String(raw);
};

const RecordDialog: React.FC<{
  title: string;
  submit: string;
  fields: Fld[];
  existing: boolean;
  initial: Record<string, string>;
  lk: Record<Lk, Map<string, string>>;
  onClose: () => void;
  onSave: (values: Record<string, any>) => Promise<string | null>;
}> = ({ title, submit, fields, existing, initial, lk, onClose, onSave }) => {
  const [v, setV] = useState<Record<string, string>>(initial);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const go = async () => {
    if (busy) return;
    const out: Record<string, any> = {};
    for (const f of fields) {
      const raw = (v[f.key] ?? "").trim();
      if (f.required && !raw) return setErr(`${f.label} is required.`);
      if (f.kind === "number") {
        if (raw !== "" && !Number.isFinite(Number(raw))) return setErr(`${f.label} must be a number.`);
        out[f.key] = raw === "" ? 0 : Number(raw);
      } else if (f.bool) {
        out[f.key] = raw === "true";
      } else {
        out[f.key] = raw;
      }
    }
    setBusy(true);
    setErr(null);
    const bad = await onSave(out);
    setBusy(false);
    if (bad) setErr(bad);
  };

  return (
    <Dialog open onOpenChange={(_, d) => !d.open && onClose()}>
      <DialogSurface className={styles.accDialog}>
        <DialogBody>
          <DialogTitle
            action={
              <DialogTrigger action="close">
                <Button appearance="subtle" aria-label="Close" icon={<Dismiss24Regular />} />
              </DialogTrigger>
            }
          >
            {title}
          </DialogTitle>
          <DialogContent>
            <div className={styles.accForm}>
              {fields.map((f) => {
                const opts: Opt[] = f.lookup ? Array.from(lk[f.lookup].entries()).map(([k, l]) => ({ v: k, l })) : f.options ?? [];
                const locked = !!f.lock && existing;
                return (
                  <div key={f.key} className={f.full ? styles.accFormFull : undefined}>
                    <Field label={f.label} required={!!f.required} hint={f.hint && !locked ? f.hint : undefined}>
                      {f.kind === "select" ? (
                        <Select disabled={locked} value={v[f.key] ?? ""} onChange={(_, d) => setV({ ...v, [f.key]: d.value })}>
                          <option value="">{f.required ? "Select…" : "None"}</option>
                          {opts.map((o) => (
                            <option key={o.v} value={o.v}>
                              {o.l}
                            </option>
                          ))}
                        </Select>
                      ) : (
                        <Input
                          disabled={locked}
                          type={f.kind === "number" ? "number" : f.kind === "date" ? "date" : "text"}
                          value={v[f.key] ?? ""}
                          onChange={(_, d) => setV({ ...v, [f.key]: d.value })}
                        />
                      )}
                    </Field>
                  </div>
                );
              })}
            </div>
            {err && (
              <div className={styles.accDlgErr} role="alert">
                {err}
              </div>
            )}
          </DialogContent>
          <DialogActions>
            <DialogTrigger action="close">
              <Button>Cancel</Button>
            </DialogTrigger>
            <Button appearance="primary" disabled={busy} onClick={go}>
              {busy ? "Working…" : submit}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

/* ---------- the page ---------- */

interface SettupFoProps {
  /** a key from FO_SETUP_GROUPS, for example "warehouses" */
  entityKey: string;
  token: string;
  baseUrl?: string;
  companyId?: string;
}

const SettupFo: React.FC<SettupFoProps> = ({ entityKey, token, baseUrl = DEFAULT_BASE, companyId = DEFAULT_COMPANY }) => {
  const api = useMemo(() => makeApi(baseUrl.replace(/\/+$/, ""), companyId, token), [baseUrl, companyId, token]);
  const entities = useMemo(() => build(api), [api]);
  const ent = entities[entityKey];

  const toasterId = useId("fo-setup-toaster");
  const { dispatchToast } = useToastController(toasterId);
  const notify = useCallback(
    (message: string, intent: Intent = "success") =>
      dispatchToast(
        <Toast>
          <ToastTitle>{message}</ToastTitle>
        </Toast>,
        { intent, position: "top-end", timeout: 4000 }
      ),
    [dispatchToast]
  );

  const [rows, setRows] = useState<Rec[]>([]);
  const [ctx, setCtx] = useState<Ctx>({ site: "", lk: { sites: new Map(), workers: new Map(), items: new Map() } });
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState<{ key: string; dir: 1 | -1 } | null>(null);
  const [form, setForm] = useState<{ rec: Rec | null } | null>(null);
  const [del, setDel] = useState<Rec | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!ent) return;
    setLoading(true);
    setLoadErr(null);
    try {
      const lk: Record<Lk, Map<string, string>> = { sites: new Map(), workers: new Map(), items: new Map() };
      let site = "";
      const needs = ent.needs ?? [];
      await Promise.all([
        ...needs.map(async (n) => {
          try {
            if (n === "sites") {
              const sites = await api.all("api/masterdata/sites");
              const s = sites.find((x) => S(x.code) === "LAG") ?? sites[0];
              site = S(s?.id);
            } else if (n === "workers") {
              (await api.all("api/workforce/workers")).forEach((w) => lk.workers.set(w.id, S(w.fullName)));
            } else {
              (await api.all("api/masterdata/items")).forEach((i) => lk.items.set(i.id, S(i.name)));
            }
          } catch {
            /* the pickers stay empty; the list itself still loads */
          }
        }),
        (async () => setRows(await ent.load()))(),
      ]);
      setCtx({ site, lk });
    } catch (e) {
      setLoadErr(errText(e));
    } finally {
      setLoading(false);
    }
  }, [ent, api]);

  useEffect(() => {
    setQ("");
    setSort(null);
    setForm(null);
    setDel(null);
    setRows([]);
    reload();
  }, [reload]);

  const cellValue = useCallback(
    (c: Col, r: Rec): any => {
      const v = c.get ? c.get(r) : r[c.key];
      return c.kind === "lookup" ? ctx.lk[c.lookup as Lk].get(S(v)) ?? "" : v;
    },
    [ctx]
  );
  const text = useCallback(
    (c: Col, r: Rec): string => {
      const v = cellValue(c, r);
      if (v === null || v === undefined || v === "") return "";
      if (c.kind === "money") return money(N(v));
      if (c.kind === "num") return N(v).toLocaleString("en-NG");
      if (c.kind === "date") return D(v);
      return String(v);
    },
    [cellValue]
  );

  const shown = useMemo(() => {
    if (!ent) return [];
    const needle = q.trim().toLowerCase();
    let out = rows.filter((r) => !needle || ent.cols.some((c) => text(c, r).toLowerCase().includes(needle)));
    if (sort) {
      const col = ent.cols.find((c) => c.key === sort.key);
      if (col) {
        out = [...out].sort((a, b) => {
          const x = cellValue(col, a);
          const y = cellValue(col, b);
          return (typeof x === "number" && typeof y === "number" ? x - y : S(x).localeCompare(S(y))) * sort.dir;
        });
      }
    }
    return out;
  }, [rows, q, sort, ent, text, cellValue]);

  const submit = async (values: Record<string, any>): Promise<string | null> => {
    if (!ent) return null;
    const bad = ent.validate ? ent.validate(values) : null;
    if (bad) return bad;
    try {
      if (form?.rec) {
        if (!ent.edit) return null;
        await change(api, ent.edit.path, form.rec.id, (e) => ent.edit!.apply(e, values));
        notify(`${cap(ent.noun)} saved`);
      } else if (ent.create) {
        await ent.create(values, rows, ctx);
        notify(`${cap(ent.noun)} created`);
      }
      setForm(null);
      await reload();
      return null;
    } catch (e) {
      return errText(e);
    }
  };

  const flip = async (r: Rec) => {
    if (!ent?.toggle || busyId) return;
    setBusyId(r.id);
    try {
      const on = r.isActive !== false;
      await change(api, ent.toggle, r.id, (e) => (e.isActive = !on));
      notify(`${S(r.name ?? r.code)} ${on ? "deactivated" : "activated"}`);
      await reload();
    } catch (e) {
      notify(errText(e), "error");
    } finally {
      setBusyId(null);
    }
  };

  const confirmDelete = async () => {
    if (!del || !ent?.remove) return;
    setBusyId(del.id);
    try {
      await api.del(`${ent.remove}/${del.id}`);
      notify(`${cap(ent.noun)} deleted`);
      setDel(null);
      await reload();
    } catch (e) {
      notify(errText(e), "error");
      setDel(null);
    } finally {
      setBusyId(null);
    }
  };

  if (!ent) return <div className={styles.accWrap}>This setup screen isn't available.</div>;

  const canAdd = !!ent.create && !ent.readOnly;
  const hasActions = !ent.readOnly && !!(ent.edit || ent.toggle || ent.remove);
  const firstRun = loading && rows.length === 0;
  const formInitial = (rec: Rec | null): Record<string, string> => {
    const f = rec && ent.toForm ? ent.toForm(rec) : null;
    return Object.fromEntries(ent.fields.map((x) => [x.key, initialOf(x, rec, ent.defaults ?? {}, f)]));
  };

  return (
    <div className={styles.accWrap}>
      <Toaster toasterId={toasterId} />

      <header className={styles.accHead}>
        <div>
          <h2 className={styles.accTitle}>{ent.title}</h2>
          <p className={styles.accSub}>{ent.subtitle}</p>
        </div>
        {canAdd && (
          <Button appearance="primary" icon={<Add20Regular />} onClick={() => setForm({ rec: null })} disabled={firstRun}>
            New {ent.noun}
          </Button>
        )}
      </header>

      {loadErr && (
        <div className={styles.accErr} role="alert">
          {loadErr}
          <Button size="small" appearance="subtle" onClick={reload}>
            Try again
          </Button>
        </div>
      )}

      <div className={styles.accTiles}>
        <div className={styles.accTile}>
          <span className={styles.accTileLabel}>{ent.title}</span>
          <span className={styles.accTileValue}>{firstRun ? "…" : rows.length.toLocaleString("en-NG")}</span>
          <span className={styles.accTileNote}>
            {rows.some((r) => "isActive" in r) ? `${rows.filter((r) => r.isActive !== false).length} active` : "In total"}
          </span>
        </div>
      </div>

      <section className={styles.accCard}>
        <div className={styles.accToolbar}>
          <div className={styles.accSearch}>
            <Search20Regular />
            <input className={styles.accSearchInput} placeholder={`Search ${ent.title.toLowerCase()}`} value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search" />
          </div>
          <span className={styles.accSpacer} />
          <span className={styles.accCount}>
            {shown.length} of {rows.length}
          </span>
          <Button appearance="subtle" icon={<ArrowSync20Regular />} onClick={reload} disabled={loading} aria-label="Refresh" title="Refresh" />
        </div>

        <div className={styles.accTableWrap}>
          <table className={styles.accTable}>
            <thead>
              <tr>
                {ent.cols.map((c) => (
                  <th
                    key={c.key}
                    className={c.right ? styles.accRight : undefined}
                    onClick={() => setSort((p) => (p && p.key === c.key ? (p.dir === 1 ? { key: c.key, dir: -1 } : null) : { key: c.key, dir: 1 }))}
                    aria-sort={sort?.key === c.key ? (sort.dir === 1 ? "ascending" : "descending") : "none"}
                  >
                    {c.label}
                    <span className={styles.accSortMark}>{sort?.key === c.key ? (sort.dir === 1 ? " ▲" : " ▼") : ""}</span>
                  </th>
                ))}
                {hasActions && <th className={styles.accRight} aria-label="Actions" />}
              </tr>
            </thead>
            <tbody>
              {firstRun &&
                [0, 1, 2, 3, 4].map((i) => (
                  <tr key={i}>
                    <td colSpan={ent.cols.length + 1}>
                      <span className={styles.accSk} style={{ width: `${60 + ((i * 11) % 30)}%`, height: 12 }} />
                    </td>
                  </tr>
                ))}
              {shown.map((r) => (
                <tr key={r.id} className={r.isActive === false ? styles.accRowOff : undefined}>
                  {ent.cols.map((c) => {
                    const v = cellValue(c, r);
                    return (
                      <td key={c.key} className={`${c.right ? styles.accRight : ""} ${c.kind === "mono" ? styles.accMono : ""}`}>
                        {c.kind === "status" ? v ? <Pill text={S(v)} /> : null : text(c, r)}
                      </td>
                    );
                  })}
                  {hasActions && (
                    <td className={styles.accRight}>
                      <span className={styles.accActions}>
                        {ent.edit && (
                          <button className={styles.accIconBtn} title="Edit" aria-label="Edit" onClick={() => setForm({ rec: r })}>
                            <Edit20Regular />
                          </button>
                        )}
                        {ent.toggle && (
                          <button className={styles.accIconBtn} disabled={busyId === r.id} title={r.isActive === false ? "Activate" : "Deactivate"} aria-label="Switch on or off" onClick={() => flip(r)}>
                            {r.isActive === false ? <Checkmark20Regular /> : <Dismiss20Regular />}
                          </button>
                        )}
                        {ent.remove && (
                          <button className={`${styles.accIconBtn} ${styles.accIconDanger}`} title="Delete" aria-label="Delete" onClick={() => setDel(r)}>
                            <Delete20Regular />
                          </button>
                        )}
                      </span>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          {!loading && shown.length === 0 && !loadErr && (
            <div className={styles.accEmpty}>
              {rows.length === 0 ? `No ${ent.title.toLowerCase()} yet.` : "Nothing matches your search."}
              {rows.length === 0 && canAdd && (
                <Button appearance="primary" size="small" icon={<Add20Regular />} onClick={() => setForm({ rec: null })}>
                  New {ent.noun}
                </Button>
              )}
            </div>
          )}
        </div>
      </section>

      {form && (
        <RecordDialog
          key={form.rec?.id ?? "new"}
          title={form.rec ? `Edit ${ent.noun}` : `New ${ent.noun}`}
          submit={form.rec ? "Save changes" : "Create"}
          fields={ent.fields}
          existing={!!form.rec}
          initial={formInitial(form.rec)}
          lk={ctx.lk}
          onClose={() => setForm(null)}
          onSave={submit}
        />
      )}

      {del && (
        <Dialog open onOpenChange={(_, d) => !d.open && setDel(null)}>
          <DialogSurface className={styles.accDialog}>
            <DialogBody>
              <DialogTitle>Delete {ent.noun}?</DialogTitle>
              <DialogContent>
                <b>{S(del.name ?? del.fullName ?? del.code ?? "This record")}</b> will be removed. This can't be undone.
              </DialogContent>
              <DialogActions>
                <DialogTrigger action="close">
                  <Button>Cancel</Button>
                </DialogTrigger>
                <Button appearance="primary" className={styles.accDangerBtn} disabled={busyId === del.id} onClick={confirmDelete}>
                  Delete
                </Button>
              </DialogActions>
            </DialogBody>
          </DialogSurface>
        </Dialog>
      )}
    </div>
  );
};

export default SettupFo;